import { ASK_AICA_CONSTANTS } from './constants';
import { CitationSource } from './citationTypes';

export interface AgentInput {
    lender_org_id: string;
    product_id: string;
    product_name: string;
}

export interface SuggestedQuestion {
    id: string;
    text: string;
    flowKey?: string;
    display?: string; // Optional display text that can differ from the actual question text
    hint?: string; // Optional smaller-font hint shown after the title (e.g. expected duration)
}

export interface ReasoningStep {
    message: string;
    timestamp: number;
    type?: 'thinking' | 'searching' | 'error';
}

export interface Message {
    id: number;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    sources?: string[];
    flowKey?: string;
    agentInput?: AgentInput;
    suggestedQuestions?: SuggestedQuestion[];
    reasoning?: ReasoningStep[] | string | null;
    hasNewReasoning?: boolean;
    error?: boolean;
    errorMessage?: string;
    metadata?: {
        llm_used?: string;
        context_available?: boolean;
        timestamp?: number;
        step_type?: 'thinking' | 'searching' | 'error';
        error?: boolean;
        errorMessage?: string;
        skills_invoked?: string[];
        file_url?: string;
        file_name?: string;
        file_type?: string;
        /** Citation sources from Athena (final chunk only). Do NOT touch legacy sources?: string[]. */
        sources?: CitationSource[];
        agg_tokens?: number;
    };

    // For messages fetched from backend
    created_at?: string;
    input_text?: string;
    status?: string;
}

export interface LibraryFile {
    fileUrl: string;
    fileName: string;
    fileType?: string;
    createdAt: string;
    source: string;
    mnemosyneDocId: string;
}

// UI-related actions
export function setAskAicaOpen(isOpen: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_ASK_AICA_OPEN,
        payload: isOpen,
    };
}

export function setAskAicaSelectedSources(sources: string[]) {
    return {
        type: ASK_AICA_CONSTANTS.SET_ASK_AICA_SELECTED_SOURCES,
        payload: sources,
    };
}

export function setAskAicaRightPanelMode(mode: 'suggestions' | 'reasoning') {
    return {
        type: ASK_AICA_CONSTANTS.SET_ASK_AICA_RIGHT_PANEL_MODE,
        payload: mode,
    };
}

// Action to add a message to the chat
export function addMessage(message: Message) {
    return {
        type: ASK_AICA_CONSTANTS.ADD_MESSAGE,
        payload: message,
    };
}

// Action to add a message to the chat
export function initialiseMessageThread(messages: Message[]) {
    return {
        type: ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD,
        payload: messages,
    };
}

// Action to update a message (used for streaming updates)
export function updateMessage(id: number, content: string, metadata?: any) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_MESSAGE,
        payload: { id, content, metadata },
    };
}

// Action to set loading state
export function setIsLoading(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_LOADING,
        payload: isLoading,
    };
}

// Action to set clearing chat state
export function setIsClearingChat(isClearingChat: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT,
        payload: isClearingChat,
    };
}

// Action to set loading state
export function setIsLoadingChatHistory(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY,
        payload: isLoading,
    };
}

// Action to clear all messages
export function clearMessages(orgId?: string) {
    return {
        type: ASK_AICA_CONSTANTS.CLEAR_MESSAGES,
        payload: orgId,
    };
}

// Action to set an error
export function setError(error: string | null) {
    return {
        type: ASK_AICA_CONSTANTS.SET_ERROR,
        payload: error,
    };
}

// Action to set reasoning for a message
export function setReasoning(messageId: number, reasoning: ReasoningStep[], hasNewReasoning: boolean = false) {
    return {
        type: ASK_AICA_CONSTANTS.SET_REASONING,
        payload: { messageId, reasoning, hasNewReasoning },
    };
}

// Action to reset reasoning
export function resetReasoning() {
    return {
        type: ASK_AICA_CONSTANTS.RESET_REASONING,
    };
}

// Action to update reasoning for a message
export function updateReasoning(messageId: number, reasoning: string | null, hasNewReasoning: boolean = false) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_REASONING,
        payload: { messageId, reasoning, hasNewReasoning },
    };
}

// Action to set global suggested questions (replaces all)
export function setGlobalSuggestedQuestions(questions: SuggestedQuestion[]) {
    return {
        type: ASK_AICA_CONSTANTS.SET_GLOBAL_SUGGESTED_QUESTIONS,
        payload: questions,
    };
}

// Action to update global suggested questions (adds to existing)
export function updateGlobalSuggestedQuestions(questions: SuggestedQuestion[]) {
    return {
        type: ASK_AICA_CONSTANTS.UPDATE_GLOBAL_SUGGESTED_QUESTIONS,
        payload: questions,
    };
}

// Action to set the Library files loading state
export function setLibraryLoading(isLoading: boolean) {
    return {
        type: ASK_AICA_CONSTANTS.SET_LIBRARY_LOADING,
        payload: isLoading,
    };
}

// Action to set the Library files (replaces all)
export function setLibraryFiles(files: LibraryFile[]) {
    return {
        type: ASK_AICA_CONSTANTS.SET_LIBRARY_FILES,
        payload: files,
    };
}

// Action to prepend newly generated file(s) to the Library list — used to reflect a
// just-generated file immediately, without refetching the full list from Polus.
export function addLibraryFiles(files: LibraryFile[]) {
    return {
        type: ASK_AICA_CONSTANTS.ADD_LIBRARY_FILES,
        payload: files,
    };
}
